import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, MapPin, Briefcase, Clock, ArrowUpRight } from "lucide-react";
import { JOBS } from "@/data/jobs";

function JobCard({ job, expanded, onToggle }) {
  const applyHref = `#apply?position=${encodeURIComponent(job.title)}`;
  return (
    <motion.article
      layout
      data-testid={`job-card-${job.slug}`}
      id={job.slug}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-8% 0px" }}
      transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
      className="border-t border-hairline last:border-b group"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`${job.slug}-panel`}
        data-testid={`job-toggle-${job.slug}`}
        className="w-full text-left py-6 md:py-8 flex items-start gap-4 md:gap-8 focus-visible:outline focus-visible:outline-1 focus-visible:outline-[color:var(--accent)]"
      >
        <div className="hidden md:block mono-label !text-white/40 pt-1 w-14 shrink-0">
          [{String(JOBS.indexOf(job) + 1).padStart(2, "0")}]
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-2xl md:text-4xl tracking-tight leading-tight group-hover:text-[color:var(--accent)] transition-colors">
            {job.title}
          </h3>
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-white/60 text-[13px]">
            <span className="inline-flex items-center gap-1.5">
              <Briefcase size={14} className="text-[color:var(--accent)]" />
              {job.experience}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={14} className="text-[color:var(--accent)]" />
              {job.location}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={14} className="text-[color:var(--accent)]" />
              Full-time
            </span>
          </div>
        </div>
        <div className="shrink-0 pt-1 md:pt-2">
          <span
            aria-hidden
            className="w-10 h-10 md:w-12 md:h-12 grid place-items-center border border-hairline group-hover:border-[color:var(--accent)] group-hover:bg-[color:var(--accent)] transition-colors"
          >
            {expanded ? <Minus size={18} /> : <Plus size={18} />}
          </span>
        </div>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            id={`${job.slug}-panel`}
            key="panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-10 md:pb-14 md:pl-[88px] grid md:grid-cols-12 gap-8 md:gap-10">
              <div className="md:col-span-7">
                <div className="mono-label !text-[color:var(--accent)] mb-2">Job summary</div>
                <p className="text-white/75 text-[14px] md:text-[15px] leading-[1.7]">
                  {job.summary}
                </p>

                <div className="mono-label !text-[color:var(--accent)] mt-8 mb-2">
                  Key responsibilities
                </div>
                <ul className="space-y-2 text-white/75 text-[14px] md:text-[15px] leading-[1.6]">
                  {job.responsibilities.map((r) => (
                    <li key={r} className="flex gap-3">
                      <span className="mt-2 h-1 w-1 bg-[color:var(--accent)] shrink-0" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="md:col-span-5">
                <div className="mono-label !text-[color:var(--accent)] mb-3">Required skills</div>
                <div className="flex flex-wrap gap-2">
                  {job.skills.map((s) => (
                    <span
                      key={s}
                      className="font-mono text-[11px] px-2.5 py-1.5 border border-hairline text-white/80"
                    >
                      {s}
                    </span>
                  ))}
                </div>

                <a
                  href={applyHref}
                  data-testid={`job-apply-${job.slug}`}
                  className="mt-8 inline-flex items-center gap-3 bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] px-5 py-3 text-[13px] font-medium tracking-tight"
                >
                  Apply for this role
                  <ArrowUpRight size={14} />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}

export default function Openings() {
  const [openId, setOpenId] = useState(JOBS[0]?.slug || null);

  return (
    <section
      id="openings"
      data-testid="openings-section"
      className="relative border-t border-hairline py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 mb-14 md:mb-20 items-end">
          <div className="md:col-span-3">
            <span className="mono-label">[02] Openings</span>
          </div>
          <div className="md:col-span-9 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-15% 0px" }}
              transition={{ duration: 0.7 }}
              className="font-display text-4xl md:text-6xl lg:text-7xl tracking-[-0.03em] leading-[0.98]"
            >
              Current career
              <br />
              <span className="text-white/40">opportunities.</span>
            </motion.h2>
            <p className="max-w-[38ch] text-white/60 text-[14px] md:text-[15px] leading-[1.6]">
              Explore our current openings and find an opportunity that matches your skills and
              career goals.
            </p>
          </div>
        </div>

        <div>
          {JOBS.map((job) => (
            <JobCard
              key={job.slug}
              job={job}
              expanded={openId === job.slug}
              onToggle={() => setOpenId(openId === job.slug ? null : job.slug)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
